import { useState } from 'react';
import { AlphabetSoundCard } from '@/components/alphabet/AlphabetSoundCard';
import type { AlphabetLetter } from '@/content/mobile';
import { ALPHABET_TABS, letterAudioSource, sectionsForTab, type AlphabetTab } from '@/lib/alphabet-sections';
import type { Voice } from '@/providers/LearnerPreferences';

type AlphabetBoardProps = {
  title: string;
  description: string;
  alphabet: Record<string, AlphabetLetter[]>;
  selected: AlphabetLetter | null;
  voice: Voice;
  onChoose: (letter: AlphabetLetter) => void;
};

export function AlphabetBoard({ title, description, alphabet, selected, voice, onChoose }: AlphabetBoardProps) {
  const [tab, setTab] = useState<AlphabetTab>('consonants');
  const sections = sectionsForTab(alphabet, tab);

  return (
    <div className="mx-auto max-w-5xl px-5 pb-10 pt-9 sm:px-8">
      <header className="text-center">
        <h1 className="font-nunito text-2xl font-bold text-[#1E1E1E]">{title}</h1>
        <p className="mx-auto mt-3 max-w-[280px] font-nunito text-sm text-[#1E1E1E]">{description}</p>
        <div
          role="tablist"
          aria-label="Letter groups"
          className="mx-auto mb-7 mt-8 flex w-full max-w-md"
        >
          {ALPHABET_TABS.map((item) => {
            const active = tab === item.id;
            return (
              <button
                key={item.id}
                type="button"
                role="tab"
                id={`alphabet-tab-${item.id}`}
                aria-selected={active}
                aria-controls={`alphabet-panel-${item.id}`}
                tabIndex={active ? 0 : -1}
                onClick={() => setTab(item.id)}
                className={`relative flex-1 pb-2.5 text-[15px] text-[#1E1E1E] focus:outline-none focus-visible:ring-4 focus-visible:ring-[#1E1E1E]/20 ${
                  active ? 'font-bold' : 'font-normal'
                }`}
              >
                {item.label}
                {active ? <span className="absolute inset-x-3 bottom-0 h-0.5 bg-[#1E1E1E]" aria-hidden /> : null}
              </button>
            );
          })}
        </div>
      </header>
      <div
        id={`alphabet-panel-${tab}`}
        role="tabpanel"
        aria-labelledby={`alphabet-tab-${tab}`}
        className="space-y-9"
      >
        {sections.map((section) => (
          <section key={section.title}>
            <h2 className="mb-3 font-nunito text-lg font-extrabold text-[#1E1E1E]">{section.title}</h2>
            <div
              data-testid={`alphabet-grid-${section.title}`}
              className="grid grid-cols-3 justify-items-center gap-5"
            >
              {section.letters.map((letter) => (
                <AlphabetSoundCard
                  key={`${section.title}-${letter.script}`}
                  letter={letter}
                  selected={selected?.script === letter.script}
                  disabled={!letterAudioSource(letter, voice)}
                  onChoose={() => onChoose(letter)}
                />
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
